<h2 mat-dialog-title class="dialog-title">
  {{ 'firmwareManagement.bulkUpdate.title' | translate }}
</h2>

<mat-dialog-content class="dialog-content">
  @if (stage() === 'form') {
    <div class="device-summary">
      <mat-icon class="summary-icon">devices</mat-icon>
      <span class="summary-text">
        {{ 'firmwareManagement.bulkUpdate.selectedDevices' | translate }}:
        <strong>{{ deviceCount }}</strong>
      </span>
    </div>

    @if (exceedsDeviceLimit) {
      <xemex-info-banner>
        {{
          'firmwareManagement.bulkUpdate.tooManyDevices'
            | translate: { count: deviceCount, max: maxDevices }
        }}
      </xemex-info-banner>
    }

    @if (hasMixedDeviceTypes) {
      <xemex-info-banner>
        {{ 'firmwareManagement.bulkUpdate.mixedDeviceTypes' | translate }}
      </xemex-info-banner>
    }

    <div class="firmware-selection">
      <form [formGroup]="form">
        <mat-form-field class="full-width xemex-select" [subscriptSizing]="'dynamic'">
          <mat-label>{{ 'firmwareManagement.updateDialog.firmwareVersion' | translate }}</mat-label>
          <mat-select formControlName="firmwareId" required>
            @for (firmware of data.availableFirmwares; track firmware.firmwareId) {
              <mat-option [value]="firmware.firmwareId">
                {{ firmware.version }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </form>
    </div>

    @if (isDlms) {
      <xemex-info-banner>
        {{ 'firmwareManagement.updateDialog.dlmsDuration' | translate }}
      </xemex-info-banner>
    }
  }

  <!-- DLMS: one request, no stream — the 202 body is the whole outcome. -->
  @if (isDlms && stage() === 'updating') {
    <div class="dlms-starting">
      <xemex-spinner></xemex-spinner>
      <span class="dlms-starting-text">
        {{ 'firmwareManagement.bulkUpdate.updating' | translate }}
      </span>
    </div>
  }

  @if (isDlms && stage() === 'complete') {
    <div class="dlms-summary">
      <p class="dlms-summary-headline">
        {{
          'firmwareManagement.bulkUpdate.dlmsSummary'
            | translate: { started: progress().succeeded, rejected: progress().failed }
        }}
      </p>
      <p class="dlms-summary-hint">
        {{ 'firmwareManagement.bulkUpdate.dlmsHint' | translate }}
      </p>

      @if (rejections().length) {
        <ul class="rejection-list">
          @for (rejection of rejections(); track rejection.deviceId) {
            <li class="rejection-item">
              <mat-icon class="rejection-icon">error_outline</mat-icon>
              <span class="rejection-device">{{ rejection.deviceLabel }}</span>
              <span class="rejection-reason">
                @if (rejection.reasonKey) {
                  {{ rejection.reasonKey | translate }}
                } @else {
                  {{ rejection.message }}
                }
              </span>
            </li>
          }
        </ul>
      }
    </div>
  }

  @if (!isDlms && (stage() === 'updating' || stage() === 'complete')) {
    <div class="progress-panel">
      <div class="progress-header">
        <span class="progress-title">
          @if (stage() === 'updating') {
            {{ 'firmwareManagement.bulkUpdate.updating' | translate }}
          } @else {
            {{ 'general.completed' | translate }}
          }
        </span>
        <span class="progress-percent">{{ progressPercent }}%</span>
      </div>

      <mat-progress-bar
        [mode]="stage() === 'updating' ? 'buffer' : 'determinate'"
        [value]="progressPercent"
        [bufferValue]="progressPercent + (progress().inProgress / progress().total) * 100"
        class="bulk-progress-bar"
      >
      </mat-progress-bar>

      <div class="progress-stats">
        <div class="stat-item total">
          <mat-icon>list</mat-icon>
          <span class="stat-label">{{ 'firmwareManagement.bulkUpdate.total' | translate }}</span>
          <span class="stat-value">{{ progress().total }}</span>
        </div>

        <div class="stat-item in-progress" [class.spinning]="progress().inProgress > 0">
          <mat-icon>sync</mat-icon>
          <span class="stat-label">{{ 'general.inProgress' | translate }}</span>
          <span class="stat-value">{{ progress().inProgress }}</span>
        </div>

        <div class="stat-item success">
          <mat-icon>check_circle</mat-icon>
          <span class="stat-label">{{ 'general.successful' | translate }}</span>
          <span class="stat-value">{{ progress().succeeded }}</span>
        </div>

        <div class="stat-item failed">
          <mat-icon>error</mat-icon>
          <span class="stat-label">{{ 'general.failed' | translate }}</span>
          <span class="stat-value">{{ progress().failed }}</span>
        </div>
      </div>
    </div>

    @if (stage() === 'complete') {
      <div class="completion-summary">
        @if (progress().failed === 0) {
          <mat-icon class="completion-icon success">check_circle</mat-icon>
          <p class="completion-text success">
            {{ 'firmwareManagement.bulkUpdate.allSucceeded' | translate }}
          </p>
        } @else if (progress().succeeded === 0) {
          <mat-icon class="completion-icon failed">error</mat-icon>
          <p class="completion-text failed">
            {{ 'firmwareManagement.bulkUpdate.allFailed' | translate }}
          </p>
        } @else {
          <mat-icon class="completion-icon partial">warning</mat-icon>
          <p class="completion-text partial">
            {{
              'firmwareManagement.bulkUpdate.partialSuccess'
                | translate: { success: progress().succeeded, total: progress().total }
            }}
          </p>
        }
      </div>
    }
  }
</mat-dialog-content>

<mat-dialog-actions align="end" class="dialog-actions">
  @if (stage() === 'form') {
    <button mat-button (click)="onCancel()" class="cancel-btn">
      {{ 'general.btnCancel' | translate }}
    </button>
    <button
      mat-raised-button
      color="primary"
      (click)="onUpdate()"
      [disabled]="!canSubmit"
      class="update-btn"
    >
      {{ 'firmwareManagement.updateDialog.updateButton' | translate }}
    </button>
  }

  @if (stage() === 'updating' && !isDlms) {
    <xemex-spinner size="sm"></xemex-spinner>
    <span class="updating-text">{{ completedCount }} / {{ progress().total }}</span>
  }

  @if (stage() === 'complete') {
    <button mat-raised-button color="primary" (click)="onClose()" class="close-btn">
      {{ 'general.btnClose' | translate }}
    </button>
  }
</mat-dialog-actions>
